import Phaser from "phaser";
import { needsHighDprCorrection } from "./highDprSync";

/**
 * PR31 (rendering sharpness). Phaser 3 used to have a top-level `resolution`
 * game-config option that rendered the canvas's drawing buffer at
 * `gameSize * resolution` physical pixels while keeping its CSS box at
 * `gameSize` - exactly what "retina"/high-DPI support needs. Phaser 3.60
 * removed it entirely from the renderer (confirmed against this project's
 * installed 3.90.0: neither `Phaser.Types.Core.GameConfig` nor
 * `Phaser.Types.Core.RenderConfig` has a `resolution` field any more, and
 * `ScaleManager`/`WebGLRenderer` never read `devicePixelRatio` anywhere).
 * With `Scale.RESIZE` (this game's mode - see `main.ts`), Phaser sets the
 * canvas's drawing-buffer `width`/`height` attributes directly to the
 * parent's CSS-pixel size, so on any `devicePixelRatio > 1` screen the
 * browser upscales that lower-resolution buffer to fill the physical
 * screen - this is the actual mechanism behind the "blurry" reports
 * (hits `Phaser.GameObjects.Text` and thin `Graphics` strokes hardest;
 * this codebase renders everything through those two APIs, with zero
 * raster image assets anywhere).
 *
 * This function reimplements the old behavior by hand, using only public,
 * typed `WebGLRenderer` API (`resize()`/`setProjectionMatrix()`/`gl`):
 *
 *  1. Resize the canvas's drawing buffer to `cssSize * dpr` physical
 *     pixels, but pin its CSS style size back to the original `cssSize` -
 *     the canvas's on-screen footprint/layout never changes.
 *  2. Call the renderer's own `resize()` AT THE PHYSICAL size first - this
 *     is the exact code path Phaser's `RESIZE` mode already calls on every
 *     ordinary resize/orientation-change, so the WebGL viewport, scissor
 *     box, and `drawingBufferHeight` bookkeeping all land in their own
 *     already-tested, mutually consistent state (just at a bigger number
 *     than usual) - no hand-rolled scissor/viewport math.
 *  3. Then override ONLY the projection matrix back to the LOGICAL
 *     (CSS-pixel) size. This is the one piece that must differ from
 *     Phaser's own default: every Scene in this app positions everything
 *     via `this.scale.width`/`this.scale.height`, which stays in CSS
 *     pixels throughout (`Phaser.Scale.ScaleManager#width` reads
 *     `gameSize.width`, which this function never touches) - so every
 *     existing layout/font-size/hit-test calculation in the codebase is
 *     completely unaffected. The projection matrix is what maps those
 *     unchanged logical coordinates onto clip space; leaving it at the
 *     logical size while the viewport/drawing-buffer are physical is
 *     exactly the standard WebGL "supersampled/retina" technique - a
 *     logical rectangle spanning the full logical size still maps to the
 *     FULL (now denser) physical buffer, not a corner of it.
 *  4. Every Scene's default Camera separately re-derives its OWN WebGL
 *     scissor box every rendered frame, straight from its own
 *     `camera.width`/`camera.height` (`WebGLRenderer#preRenderCamera()` ->
 *     `pushScissor(camera.x, camera.y, camera.width, camera.height)`) -
 *     NOT from the renderer/canvas size step 2 sets. Those camera
 *     dimensions come from the Scale Manager's logical `gameSize`
 *     (deliberately unchanged above, still CSS pixels), so left alone,
 *     every camera's scissor box stays clipped to the original small
 *     logical rectangle - confirmed empirically (headless Chromium + live
 *     `gl.getParameter(gl.SCISSOR_BOX)` inspection) to otherwise clip all
 *     rendered content into one corner of the enlarged canvas. Resizing
 *     each camera's `width`/`height` alone (to the PHYSICAL pixel size,
 *     NOT its zoom - explicitly tried and confirmed WRONG empirically:
 *     combining a resized camera viewport with `setZoom(dpr)` double-
 *     applies the DPR factor, since the global projection matrix set in
 *     step 3 already does the one and only coordinate scale-up needed)
 *     fixes the scissor box to match the buffer while leaving world-
 *     content positioning (every Scene still uses the unchanged logical
 *     `this.scale.width`/`height`) completely alone. Phaser's own
 *     pointer-to-world transform keeps working unchanged with the camera
 *     at its default zoom of 1 - verified empirically via a real button
 *     click (menu navigation) at `deviceScaleFactor: 2`. This project
 *     creates exactly one camera per Scene (`cameras.main`, confirmed via
 *     project-wide search - no `cameras.add()` anywhere), and every Scene
 *     is pre-registered at boot (`main.ts`'s `scene: [...]` array), so
 *     each Scene's `sys.cameras`/`cameras.main` already exists here even
 *     for a Scene that hasn't been started/made visible yet.
 *
 * WHEN this reapplies - deliberately NOT just "on `Scale.Events.RESIZE`",
 * despite that being the obviously-tempting hook. `ScaleManager` itself
 * re-emits that SAME event from more than one internal path (its own
 * native `orientationchange`/`resize` DOM listeners, AND a ~500ms
 * defensive poll in its per-frame `step()`), and Phaser's OWN internal
 * `WebGLRenderer#onResize` listener - attached during renderer boot,
 * before this module's `applyHighDprCanvas()` call in `main.ts` ever runs
 * - is NOT reliably ordered relative to this module's own listener on
 * every one of those paths. Empirically (headless Chromium + intercepting
 * `gl.viewport()` calls with a captured stack trace) a genuine orientation
 * -change resize showed THIS module's correction firing, immediately
 * followed by Phaser's OWN `WebGLRenderer#onResize` firing AFTER it and
 * silently resetting the viewport straight back to the logical size -
 * exactly the "content only fills one corner" bug, but only reachable via
 * a REAL post-boot resize (a live device rotation, not just a fixed
 * `deviceScaleFactor` at page load), which is why it wasn't caught by
 * this project's earlier initial-load-only verification. Rather than
 * depend on winning a listener-ordering race against Phaser's own
 * internals across every path that can re-emit `Scale.Events.RESIZE`,
 * this instead self-heals once per rendered frame: `Phaser.Core.Events.STEP`
 * fires every frame, right after `ScaleManager`'s own `PRE_STEP`-driven
 * poll/refresh has already fully run for that frame (`ScaleManager.step`
 * is itself a `PRE_STEP` listener, and `STEP` fires immediately after
 * `PRE_STEP` in `Game#step()`), so by the time this runs, whatever Phaser
 * did to the renderer/cameras this frame has already happened - this is
 * always the frame's last word. A cheap comparison
 * (`renderer.width`/`canvas.width` against the expected physical size,
 * AND every currently active Scene's own main camera - see
 * `maybeCorrect()`'s own doc for why the camera check is required, not
 * optional) skips the actual correction work on every frame where nothing
 * needs fixing, so the steady-state per-frame cost is a handful of
 * integer comparisons (one Scene list iteration, typically 1-2 active
 * Scenes), not a real GL/camera resize - the real work only runs on the
 * rare frame right after something actually changed.
 *
 * Intentionally does nothing (leaves Phaser's default, unchanged, already-
 * shipped-and-working behavior in place) for:
 *  - `dpr <= 1` (ordinary desktop monitors) - zero behavior change.
 *  - the Canvas2D fallback renderer (`type: Phaser.AUTO` picks it only when
 *    WebGL is unavailable - vanishingly rare in production today). Bringing
 *    that path to the same sharpness would need separate, un-tested-here
 *    2D-context-transform logic for a renderer path this app essentially
 *    never actually uses; not worth the added risk for this PR.
 *
 * This project has no raster image assets, particle emitters, post-FX
 * pipelines, or render-textures/dynamic-textures anywhere (confirmed via
 * project-wide search) - the only WebGL feature this needs to coexist with
 * is `createGeometryMask()` (used by a few scrollable UI lists), which is
 * stencil-based and does not go through the renderer's separate
 * render-target capture path (`beginCapture`/`endCapture`, which DOES
 * reset the projection matrix back to `renderer.width/height` - the one
 * thing that would silently undo this override). Verified empirically
 * (headless Chromium, `deviceScaleFactor: 2`) against the gameplay screen,
 * a masked scrollable list screen, AND a live orientation-change resize
 * mid-session - see PR31's manual-QA notes for what still requires a real
 * high-DPI device.
 */
export function applyHighDprCanvas(game: Phaser.Game, dpr: number): void {
  if (dpr <= 1) return;

  const correct = (): void => {
    const renderer = game.renderer;
    if (!(renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return;

    const cssWidth = game.scale.width;
    const cssHeight = game.scale.height;
    const pixelWidth = Math.round(cssWidth * dpr);
    const pixelHeight = Math.round(cssHeight * dpr);

    const canvas = game.canvas;
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    renderer.resize(pixelWidth, pixelHeight);
    renderer.setProjectionMatrix(cssWidth, cssHeight);

    for (const scene of game.scene.scenes) {
      const camera = scene.sys.cameras?.main;
      if (camera) camera.setSize(pixelWidth, pixelHeight);
    }
  };

  /**
   * Cheap per-frame check - see this function's own doc for why a one-shot
   * resize-event hook isn't reliable here. Only runs the real `correct()`
   * work when something is actually out of sync.
   *
   * PR31 follow-up fix (quarter-screen bug on scene re-entry). The
   * original version of this check compared only `renderer.width/height`
   * and `canvas.width/height` against the expected physical size - both
   * are properties of the single, persistent `Phaser.Game`/canvas that
   * `correct()` already fixed once and that never change again on their
   * own, so once correct, this check kept passing forever and this
   * function stopped doing anything at all, even though `correct()` is
   * ALSO the only place that resizes each Scene's own camera. A Scene
   * that gets destroyed and recreated (Endless PLAY AGAIN, Campaign
   * RETRY/re-entering a level - any `scene.start()` back into `GameScene`)
   * gets a brand new default camera, sized at the LOGICAL (CSS-pixel)
   * size, not the physical one - and because the canvas/renderer were
   * already "correct" from the first Scene's own correction, this check
   * silently skipped `correct()` forever after, leaving the new camera's
   * own scissor/viewport stuck at half width * half height of the actual
   * canvas - i.e. rendering into one quarter of the screen. Now also
   * checks every currently active Scene's own main camera dimensions, so
   * a freshly-created Scene with an un-corrected camera is detected and
   * `correct()` runs again to fix it, exactly as it did for the very
   * first Scene.
   */
  const maybeCorrect = (): void => {
    const renderer = game.renderer;
    if (!(renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) return;

    const pixelWidth = Math.round(game.scale.width * dpr);
    const pixelHeight = Math.round(game.scale.height * dpr);

    const needsCorrection = needsHighDprCorrection(
      {
        rendererWidth: renderer.width,
        rendererHeight: renderer.height,
        canvasWidth: game.canvas.width,
        canvasHeight: game.canvas.height,
        cameraDimensions: game.scene.scenes.map((scene) => {
          const camera = scene.sys.cameras?.main;
          return camera ? { width: camera.width, height: camera.height } : null;
        }),
      },
      { width: pixelWidth, height: pixelHeight },
    );
    if (!needsCorrection) return;

    correct();
  };

  game.events.once(Phaser.Core.Events.READY, maybeCorrect);
  game.events.on(Phaser.Core.Events.STEP, maybeCorrect);
}

/**
 * PR31 (rendering sharpness), part 2. `applyHighDprCanvas()` above sharpens
 * `Graphics`-drawn shapes (the board/grid/snake/fruit/obstacles/gate) by
 * densifying the WebGL drawing buffer, but `Phaser.GameObjects.Text`
 * renders to its OWN separate, private, offscreen 2D canvas first (a text
 * bitmap that then gets uploaded as a texture) - that offscreen canvas's
 * own resolution is controlled by a per-Text-object `style.resolution`
 * (still fully supported in Phaser 3.90; it just isn't inherited from a
 * global game config any more - `TextStyle`'s own default is a hardcoded
 * `1` for any Text object that doesn't set one, confirmed by reading
 * `Text.js`/`TextStyle.js` directly). This game creates `Text` objects via
 * `this.add.text(...)` in well over a dozen files (every HUD label, menu,
 * button, overlay) with no shared style-building helper to change in one
 * place, so - rather than touching every one of those call sites - this
 * wraps the single shared factory function every one of them ultimately
 * calls (`Phaser.GameObjects.GameObjectFactory.prototype.text`, the same
 * function `Phaser.GameObjects.GameObjectFactory.register('text', ...)`
 * installs) to inject a `resolution` default equal to the capped DPR,
 * WITHOUT overriding a style that explicitly sets its own `resolution`.
 * Must run once, before any Scene's `create()` calls `this.add.text(...)`
 * for the first time - called synchronously in `main.ts`, before
 * `new Phaser.Game()`.
 */
export function applyHighDprTextDefault(dpr: number): void {
  if (dpr <= 1) return;

  const factory = Phaser.GameObjects.GameObjectFactory.prototype;
  const originalText = factory.text;

  factory.text = function (
    x: number,
    y: number,
    text: string | string[],
    style?: Phaser.Types.GameObjects.Text.TextStyle,
  ): Phaser.GameObjects.Text {
    return originalText.call(this, x, y, text, { resolution: dpr, ...style });
  };
}
